import { PlayerAvatar } from "../../components/player-avatar";
import { TOOLS, type Tool } from "@saboteur/contracts/cards";
import type { DisplayProjection } from "@saboteur/contracts";
import { ToolIcon } from "../cards/card-face";

export interface PlayerToolAction {
  kind: "BREAK_TOOL" | "REPAIR_TOOL";
  participantIds: readonly string[];
  tools: readonly Tool[];
  onSelect: (participantId: string, tool: Tool) => void;
}

export function PlayerStatus({
  participant: p,
  self = false,
  toolAction,
}: {
  participant: DisplayProjection["participants"][number];
  self?: boolean;
  toolAction?: PlayerToolAction | undefined;
}) {
  return (
    <>
      <div className="player-summary">
        <PlayerAvatar seat={p.avatarIndex ?? p.seatIndex} />
        <div>
          <strong title={p.displayName}>{p.displayName}</strong>
          <small className="player-gold">
            {p.goldTotal !== undefined ? `${p.goldTotal} gold` : "Gold unavailable"}
          </small>
          {!p.connected && <small>Offline</small>}
        </div>
      </div>
      <div className="player-tools">
        {TOOLS.map((tool) => {
          const broken = p.brokenTools.includes(tool);
          const eligible =
            toolAction?.participantIds.includes(p.participantId) &&
            toolAction.tools.includes(tool) &&
            (toolAction.kind === "BREAK_TOOL" ? !broken : broken);
          if (eligible && toolAction)
            return (
              <button
                key={tool}
                className="player-tool-target"
                type="button"
                aria-label={`Select ${toolAction.kind === "BREAK_TOOL" ? "break" : "repair"} ${self ? "your" : `${p.displayName}’s`} ${tool.toLowerCase()}`}
                onClick={() => toolAction.onSelect(p.participantId, tool)}
              >
                <ToolIcon tool={tool} broken={broken} />
              </button>
            );
          return (
            <span key={tool}>
              <ToolIcon tool={tool} broken={broken} />
            </span>
          );
        })}
      </div>
    </>
  );
}

export function PlayerStrip({
  participants,
  activeParticipantId,
  selfParticipantId,
  toolAction,
}: {
  participants: DisplayProjection["participants"];
  activeParticipantId: string | null;
  selfParticipantId?: string;
  toolAction?: PlayerToolAction | undefined;
}) {
  return (
    <ol className="player-strip" aria-label="Other players and tools">
      {[...participants]
        .filter((p) => p.participantId !== selfParticipantId)
        .sort((a, b) => a.seatIndex - b.seatIndex)
        .map((p) => (
          <li
            key={p.participantId}
            aria-current={p.participantId === activeParticipantId ? "true" : undefined}
            aria-label={`${p.displayName}${p.participantId === activeParticipantId ? ", current turn" : ""}`}
            className={p.participantId === activeParticipantId ? "is-active" : ""}
          >
            <PlayerStatus participant={p} toolAction={toolAction} />
          </li>
        ))}
    </ol>
  );
}
